/**
 * Per-edge attribute actions for the edit slice — Mark / Clear seam and
 * sharp, plus per-edge crease weight editing.
 *
 * All three edit paths share a pattern: the mutation affects
 * `PolygonTopology` (flags or creases) but leaves vertex count / edge
 * indices / face connectivity intact. That means they deliberately bypass
 * `runEditOp` / `applyResult` (which own vertex-provenance reconciliation
 * and full attribute remapping) in favor of a lighter pipeline:
 *
 *   1. Resolve the target edges via `resolveSelectedEdges` — Blender-style
 *      derivation so face- and vertex-mode selections work too.
 *   2. Produce a new topology with `withEdgeFlagsSet` / `withEdgeFlagsCleared`
 *      / `withEdgeCreases` (no-op fast-paths when the write would be
 *      idempotent).
 *   3. Optionally push an undo snapshot (one-shot ops like seam/sharp do;
 *      continuous slider-driven edits defer to {@link useDeferredUndo}
 *      which pushes once per drag session).
 *   4. Recompute loop normals iff `EDGE_SHARP` flipped — the Rust
 *      auto-smooth pipeline reads that flag (`wasm/src/normals.rs`).
 *      Crease writes never touch normals.
 *   5. Strip the single-mesh `generator` ref and cascade the bake to any
 *      owning collection via `replaceSceneObjectAfterEdit`.
 *   6. Preserve `em.selectedEdges` — edge indices stay valid under
 *      attribute-only edits.
 */

import { resolveSelectedEdges } from "../../modeling/editSelection.ts";
import {
  withEdgeCreases,
  withEdgeFlagsCleared,
  withEdgeFlagsSet,
} from "../../modeling/topology.ts";
import type { SceneObject } from "../../modeling/types.ts";
import { EDGE_SEAM, EDGE_SHARP } from "../../modeling/types.ts";
import { captureSnapshot, undoManager } from "../storeUndo.ts";
import {
  computeNormalsForObject,
  type Get_,
  replaceSceneObjectAfterEdit,
  type Set_,
  traceEditOp,
} from "./core.ts";

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Commit a topology-only edit that preserves vertex / edge / face identity.
 * Shared between flag toggles and crease writes.
 *
 * `pushUndo: true` is the default for one-shot button-style ops. Continuous
 * slider-driven edits pass `false` and the UI pushes a single snapshot via
 * `useDeferredUndo` at the start of the drag session instead — otherwise
 * every tick would spam the undo stack.
 */
function commitTopologyAttrEdit(
  get: Get_,
  set: Set_,
  objectId: string,
  obj: SceneObject,
  nextTopology: SceneObject["topology"],
  normalsDirty: boolean,
  label: string,
  pushUndo: boolean,
): void {
  traceEditOp(label, get().editMode!);
  if (pushUndo) undoManager.push(captureSnapshot(get()));

  const { generator: _gen, ...rest } = obj;
  const patched: SceneObject = {
    ...(rest as SceneObject),
    topology: nextTopology,
    ...(normalsDirty
      ? computeNormalsForObject(obj, { topology: nextTopology })
      : {}),
  };

  set({
    ...replaceSceneObjectAfterEdit(get(), objectId, patched),
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  });
}

/**
 * The single mutation path for `topology.edgeFlags`. Returns silently when
 * edit mode is inactive, targets a different object, has no resolvable edge
 * selection, or would not flip any bits.
 */
function patchEdgeFlags(
  get: Get_,
  set: Set_,
  objectId: string,
  mask: number,
  op: "set" | "clear",
  label: string,
): void {
  const em = get().editMode;
  if (!em || em.objectId !== objectId) return;
  const obj = get().sceneObjects.get(objectId);
  if (!obj) return;

  const targetEdges = resolveSelectedEdges(obj.topology, em);
  if (targetEdges.size === 0) return;

  const nextTopology =
    op === "set"
      ? withEdgeFlagsSet(obj.topology, targetEdges, mask)
      : withEdgeFlagsCleared(obj.topology, targetEdges, mask);
  if (nextTopology === obj.topology) return;

  const normalsDirty = (mask & EDGE_SHARP) !== 0;
  commitTopologyAttrEdit(
    get,
    set,
    objectId,
    obj,
    nextTopology,
    normalsDirty,
    label,
    true,
  );
}

/**
 * Assign crease weight `value` (clamped to [0, 1]) to every resolved edge in
 * the current selection.
 *
 * Does NOT push its own undo snapshot: the UI drives this from a slider and
 * uses `useDeferredUndo` to snapshot once per drag session, so per-tick
 * writes don't explode the undo stack. Snapshot-push ordering: the caller's
 * `useDeferredUndo.pushOnce()` runs before the first `setEdgeCrease` call
 * of a session, capturing the pre-drag state.
 */
function patchEdgeCreases(
  get: Get_,
  set: Set_,
  objectId: string,
  value: number,
  label: string,
): void {
  const em = get().editMode;
  if (!em || em.objectId !== objectId) return;
  const obj = get().sceneObjects.get(objectId);
  if (!obj) return;

  const targetEdges = resolveSelectedEdges(obj.topology, em);
  if (targetEdges.size === 0) return;

  const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
  const edgeCount = obj.topology.edges.length / 2;
  const src = obj.topology.edgeCreases;

  // Fast no-op: every target edge already holds `clamped` (and if `clamped`
  // is 0, the source is missing or zeroed everywhere).
  let dirty = false;
  if (!src && clamped !== 0) {
    dirty = true;
  } else if (src) {
    for (const ei of targetEdges) {
      if (ei < 0 || ei >= edgeCount) continue;
      if (src[ei] !== clamped) {
        dirty = true;
        break;
      }
    }
  }
  if (!dirty) return;

  // Materialize the working array. Allocate fresh when the source is absent,
  // otherwise clone so any snapshot captured by `useDeferredUndo.pushOnce()`
  // keeps its own buffer.
  const next = src ? new Float32Array(src) : new Float32Array(edgeCount);
  for (const ei of targetEdges) {
    if (ei < 0 || ei >= edgeCount) continue;
    next[ei] = clamped;
  }

  // Honor "omit-when-empty": drop the array when every weight is 0.
  let anyNonZero = false;
  for (let i = 0; i < next.length; i++) {
    if (next[i] !== 0) {
      anyNonZero = true;
      break;
    }
  }
  const nextTopology = withEdgeCreases(
    obj.topology,
    anyNonZero ? next : undefined,
  );
  if (nextTopology === obj.topology) return;

  commitTopologyAttrEdit(
    get,
    set,
    objectId,
    obj,
    nextTopology,
    false,
    label,
    false,
  );
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createEdgeAttrOps(set: Set_, get: Get_) {
  return {
    markEdgeSeam: (objectId: string) =>
      patchEdgeFlags(get, set, objectId, EDGE_SEAM, "set", "markEdgeSeam"),
    clearEdgeSeam: (objectId: string) =>
      patchEdgeFlags(get, set, objectId, EDGE_SEAM, "clear", "clearEdgeSeam"),
    markEdgeSharp: (objectId: string) =>
      patchEdgeFlags(get, set, objectId, EDGE_SHARP, "set", "markEdgeSharp"),
    clearEdgeSharp: (objectId: string) =>
      patchEdgeFlags(get, set, objectId, EDGE_SHARP, "clear", "clearEdgeSharp"),

    /**
     * Write the crease weight (clamped to [0, 1]) to every resolved edge.
     * Intended for slider-driven live preview — this action does NOT push
     * its own undo snapshot. The caller is responsible for pushing one
     * snapshot per drag session (see {@link useDeferredUndo}).
     */
    setEdgeCrease: (objectId: string, value: number) =>
      patchEdgeCreases(get, set, objectId, value, "setEdgeCrease"),
  };
}
